import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { ActivityReportsPort } from '../../../app/ports/reports/app-activity-reports.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';

import { ActivityReportRequest } from '../../../domain/models/reports/request/app-activity-report.request';
import { GroupedActivityReportRequest } from '../../../domain/models/reports/request/grouped-app-activity-report.request';
import { ActivityReportResponse } from '../../../domain/models/reports/activity-report.model';
import { AppsTopWithPercentageResponse } from '../../../domain/models/reports/apps-top-percentage.model';

import {
    mapSdkActivityListResponseToDomain,
    mapSdkAppsTopWithPercentageResponseToDomain,
} from '../../mappers/reports/app-activity-reports.mapper';

import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';
import { activityReportsSdkMock } from '../../mocks/report/app-activity-reports.mock';

type ActivityReportsMockAction = 'getAppActivitiesList' | 'getAppsTopWithPercentage';

@Injectable({ providedIn: 'root' })
export class AppActivityReportsPortAdapter implements ActivityReportsPort {
    constructor(
        private appActivityReportsService: ReportSdk.AppActivityReportsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getAppActivitiesList(payload: ActivityReportRequest): Observable<ActivityReportResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-activity-app');

        const sdkPayload: ReportSdk.ActivityRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.page !== undefined ? { page: payload.page } : {}),
            ...(payload.size !== undefined ? { size: payload.size } : {}),
        };

        const source$: Observable<ReportSdk.AppActivityListResponse> = this.shouldMock('getAppActivitiesList')
            ? of(
                activityReportsSdkMock.getAppActivitiesList(
                    sdkPayload.employeeId,
                    sdkPayload.dateRange,
                    sdkPayload.page ?? 0,
                    sdkPayload.size ?? 20,
                ),
            ).pipe(delay(360))
            : this.appActivityReportsService.getAppActivitiesList(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkActivityListResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-activity-app')),
        );
    }

    getAppsTopWithPercentage(payload: GroupedActivityReportRequest): Observable<AppsTopWithPercentageResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-activity-app-top');

        const sdkPayload: ReportSdk.GroupedActivityRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
        };

        const source$: Observable<ReportSdk.AppsTopWithPercentageResponse> = this.shouldMock('getAppsTopWithPercentage')
            ? of(
                activityReportsSdkMock.getAppsTopWithPercentage(
                    sdkPayload.employeeId,
                    sdkPayload.dateRange,
                ),
            ).pipe(delay(360))
            : this.appActivityReportsService.getAppsTopWithPercentage(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAppsTopWithPercentageResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-activity-app-top')),
        );
    }

    private shouldMock(action: ActivityReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.appActivityReports?.[action]);
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
